import { Hub } from "common/models";
import { CreatePointHub } from "points/models";
import { distanceMiles, HUB_RADIUS_MILES } from "points/pointValidation";

/**
 * Which hub serves a new location, how far it is, and whether any hub can serve it at all.
 *
 * The add-a-point screen used to ask for the hub FIRST and bound the address search to 100 miles
 * around it. That was not decoration -- it stopped someone filling in a whole form against a
 * place no hub could serve -- but it asked the operator to answer, before they had said where the
 * place was, a question the place itself answers. So the hub is now chosen for them from the
 * location, and this module is the part that decides.
 *
 * Kept free of React and of the network, because the three things most worth getting right here
 * are all pure: which hub is nearest, the metres/seconds -> miles/minutes conversion that lands in
 * geo.ClientPointToHub, and the 100-mile rule that has to replace the old search bound.
 */

/**
 * How many hubs are worth asking Mapbox about.
 *
 * Directions is a network call per hub and this runs on every pin drag, so the field is narrowed
 * by great-circle distance first. Three is enough: road and crow-flies orderings disagree about
 * which of two nearby hubs wins, but not by enough to push the true nearest hub out of the top
 * few, and a client with a dozen hubs would otherwise cost a dozen calls per drag.
 */
export const ROAD_MEASURE_LIMIT = 3;

const METERS_PER_MILE = 1609.344;
const SECONDS_PER_MINUTE = 60;

/** What Mapbox Directions gave for one hub -> location route. Nulls when it did not answer. */
export interface RouteMeasurement {
  distanceMeters: number | null;
  durationSeconds: number | null;
}

/** A hub in the running, with whatever Mapbox managed to say about driving to the location. */
export interface HubCandidate {
  hub: Hub;
  /** Great-circle miles. Always known -- worked out here, never from the network. */
  straightLineMiles: number;
  /** Road miles from Directions, rounded. Null when Mapbox did not answer for this hub. */
  roadMiles: number | null;
  /** Drive time in minutes from Directions, rounded. Null on the same terms. */
  driveTimeMinutes: number | null;
}

/** What to call a hub on screen. Name is optional on the model; the location number is not. */
export const hubLabel = (hub: Hub | null | undefined): string => {
  if (!hub) return "";
  return hub.name || `Hub ${hub.locationNumber ?? hub.id}`;
};

/** A hub with no usable coordinate cannot be measured or ranked, so it is not a candidate. */
export const hasUsableCoordinate = (hub: Hub | null | undefined): boolean =>
  Boolean(hub) && Number.isFinite(hub?.latitude) && Number.isFinite(hub?.longitude);

/**
 * Directions reports route length in METRES; geo.ClientPointToHub.Distance is road miles as an
 * int.
 *
 * That the column is road miles and not a stored great-circle figure was checked against real
 * rows -- 120 mi where the crow-flies distance is 107.1, 165 against 152.5, 28 against 26.1 --
 * which is the road detour you would expect and not something a straight-line calculation could
 * produce.
 *
 * Null in, null out. A failed lookup has to reach the server as null so it falls back to its own
 * placeholder; a zero would read as "the hub is at the location", which is a wrong number in the
 * column mileage bills on rather than a missing one.
 */
export const roadMilesFromMeters = (meters: number | null | undefined): number | null => {
  if (typeof meters !== "number" || !Number.isFinite(meters) || meters < 0) return null;
  return Math.round(meters / METERS_PER_MILE);
};

/** Directions reports duration in SECONDS; geo.ClientPointToHub.DriveTime is minutes as an int. */
export const driveMinutesFromSeconds = (seconds: number | null | undefined): number | null => {
  if (typeof seconds !== "number" || !Number.isFinite(seconds) || seconds < 0) return null;
  return Math.round(seconds / SECONDS_PER_MINUTE);
};

/** Pairs a hub with its straight-line distance and whatever Directions said about the drive. */
export const toHubCandidate = (
  hub: Hub,
  latitude: number,
  longitude: number,
  route?: RouteMeasurement | null
): HubCandidate => ({
  hub,
  straightLineMiles: distanceMiles(hub.latitude, hub.longitude, latitude, longitude),
  roadMiles: roadMilesFromMeters(route?.distanceMeters),
  driveTimeMinutes: driveMinutesFromSeconds(route?.durationSeconds),
});

/**
 * The hubs worth asking Mapbox about, nearest first.
 *
 * Only hubs inside the radius are shortlisted: one further away cannot serve the location at all,
 * so its road distance is not a number anybody needs.
 */
export const shortlistHubs = (
  hubs: Hub[],
  latitude: number,
  longitude: number,
  limit: number = ROAD_MEASURE_LIMIT
): Hub[] => {
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return [];

  return (hubs ?? [])
    .filter(hasUsableCoordinate)
    .map((hub) => ({ hub, miles: distanceMiles(hub.latitude, hub.longitude, latitude, longitude) }))
    .filter((entry) => entry.miles <= HUB_RADIUS_MILES)
    .sort((a, b) => a.miles - b.miles)
    .slice(0, Math.max(0, limit))
    .map((entry) => entry.hub);
};

/**
 * The hub to default to.
 *
 * Road distance decides whenever Mapbox answered for anything, because road distance is the
 * question being asked: two hubs can sit the same crow-flies distance away with a river, a range
 * or a yard between the van and one of them.
 *
 * A candidate Mapbox could NOT route to is passed over while any routed candidate exists, rather
 * than being compared on its straight-line figure. The two are not the same measurement, and
 * ranking them against each other would let an unrouteable hub win on the cheaper number.
 *
 * When Mapbox answered for nothing at all -- an outage, a missing key -- every candidate falls
 * back to straight-line together, which is the comparison the old hub-first screen made anyway.
 * Degrading that way is deliberate: an outage must not stop someone adding a location.
 */
export const selectNearestHub = (candidates: HubCandidate[]): HubCandidate | null => {
  const usable = (candidates ?? []).filter((candidate) => hasUsableCoordinate(candidate.hub));
  if (usable.length === 0) return null;

  const routed = usable.filter((candidate) => candidate.roadMiles !== null);
  const pool = routed.length > 0 ? routed : usable;
  const rank = (candidate: HubCandidate): number =>
    routed.length > 0 ? (candidate.roadMiles as number) : candidate.straightLineMiles;

  return pool.reduce((best, candidate) => (rank(candidate) < rank(best) ? candidate : best));
};

/** Whether any hub can serve a coordinate, and if not, what to tell the operator. */
export type HubRadiusVerdict =
  | { status: "ok"; hub: Hub; miles: number }
  | { status: "tooFar"; hub: Hub; miles: number; message: string }
  | { status: "noHubs"; message: string };

/**
 * Can any of this client's hubs serve this coordinate?
 *
 * This is the guard the hub-first flow used to give us for free by bounding the search, which is
 * why it has to be answered the moment a location is picked rather than on save. geo.
 * CreateClientPoint attaches a point to the nearest active hub serving the client within 100 miles
 * and gives up beyond that: a location outside every hub's radius saves, gets no
 * ClientPointToHub row, and therefore has no PointOfServiceID -- and a trip references
 * PointOfServiceIDs. It would exist and be undispatchable.
 *
 * Measured great-circle, NOT by road, and deliberately so. The server's own rule is a
 * straight-line one and road distance is always the longer of the two, so judging on road miles
 * would refuse locations the server would have accepted. This check can only ever be as permissive
 * as the server, never less.
 */
export const evaluateHubRadius = (hubs: Hub[], latitude: number, longitude: number): HubRadiusVerdict => {
  const usable = (hubs ?? []).filter(hasUsableCoordinate);

  if (usable.length === 0) {
    return {
      status: "noHubs",
      message:
        "This client has no hubs with coordinates on record, so nothing can be set up to serve a " +
        "location. A hub has to be added before locations can be filed against this client.",
    };
  }

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return {
      status: "noHubs",
      message: "There is no coordinate to measure from yet.",
    };
  }

  const nearest = usable
    .map((hub) => ({ hub, miles: distanceMiles(hub.latitude, hub.longitude, latitude, longitude) }))
    .reduce((best, entry) => (entry.miles < best.miles ? entry : best));

  if (nearest.miles > HUB_RADIUS_MILES) {
    return {
      status: "tooFar",
      hub: nearest.hub,
      miles: nearest.miles,
      message:
        `The nearest hub serving this client is ${hubLabel(nearest.hub)}, about ` +
        `${Math.round(nearest.miles)} miles away. A location has to be within ${HUB_RADIUS_MILES} ` +
        "miles of a hub to be served from it, so this one cannot be added. Pick somewhere closer, " +
        "or get a hub set up for this area first.",
    };
  }

  return { status: "ok", hub: nearest.hub, miles: nearest.miles };
};

const toCreatePointHub = (candidate: HubCandidate): CreatePointHub => ({
  hubId: Number(candidate.hub.id),
  distanceMiles: candidate.roadMiles,
  driveTimeMinutes: candidate.driveTimeMinutes,
});

/**
 * The hubs to send with the create, primary first.
 *
 * A null distance or drive time is a real answer, not a gap to paper over: it says Mapbox did not
 * tell us, and the server then writes its own placeholder. Sending a guess instead -- a
 * straight-line figure dressed up as road miles -- would put a wrong number in the column mileage
 * bills on, which is worse than an obviously unset one.
 *
 * The optional second hub is dropped when it is unset or repeats the primary, so the request never
 * carries the same hub twice.
 */
export const buildCreatePointHubs = (
  primary: HubCandidate | null | undefined,
  secondary?: HubCandidate | null
): CreatePointHub[] => {
  const entries: CreatePointHub[] = [];
  if (primary) entries.push(toCreatePointHub(primary));

  if (secondary && (!primary || Number(secondary.hub.id) !== Number(primary.hub.id))) {
    entries.push(toCreatePointHub(secondary));
  }

  return entries;
};
